import React from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import {
  BackIcon,
  ExtendArrows,
  PlusIcon,
  Star02,
  TemplateLines,
} from 'components/SVG';
import CreateSparkHeaderOnboarding from './CreateSparkHeaderOnboarding';
import { useAddSparkStep } from 'hooks/SparkBuilder/sparkStep';
import { useAddSpark } from 'hooks/spark';
import { useDialog, useSafeNavigate, useWorkspaceContext } from 'hooks/common';
import { useSparkStepTypes } from 'hooks/EntityTypes/useSparkStepTypes';
import { useSparkBuilderStore } from 'stores';
import { v4 } from 'uuid';
import { useReactFlowStore } from './SparkBuilderCanvas/hooks';
import { SPARK_ROUTES } from 'constants/routes';
import CreateWithAI from './Modals/CreateWithAI';

const ACTIONS = {
  START_FROM_SCRATCH: 'startFromScratch',
  CREATE_WITH_AI: 'createWithAI',
  EXTEND_A_SPARK: 'extendSpark',
  USE_A_TEMPLATE: 'useTemplate',
} as const;

type ActionType = (typeof ACTIONS)[keyof typeof ACTIONS];

type Options = {
  title: string;
  text: string;
  icon?: React.ReactNode;
  action: ActionType;
};
const cards: Options[] = [
  {
    title: 'Create with AI',
    text: "Tell us what you'd like to build",
    action: ACTIONS.CREATE_WITH_AI,
    icon: <Star02 color='var( --color-primary)' />,
  },
  {
    title: 'Extend a Spark',
    text: 'Add capabilities to an existing Spark',
    action: ACTIONS.EXTEND_A_SPARK,
    icon: <ExtendArrows width='24' height='24' />,
  },
  {
    title: 'Use a template',
    text: 'Create an existing Spark',
    action: ACTIONS.USE_A_TEMPLATE,
    icon: <TemplateLines width='24' height='24' />,
  },
  {
    title: 'Start from scratch',
    text: 'Start from a blank canvas',
    icon: <PlusIcon height='20' width='20' />,
    action: ACTIONS.START_FROM_SCRATCH,
  },
];

function CreateSparkOnboarding() {
  const [sparkName, setSparkName] = React.useState('My New Spark');
  const { addSelectedNodes } = useReactFlowStore();
  const { mutateAsync: addSpark, isLoading: isAddSparkLoading } = useAddSpark();
  const { currentWorkspaceID } = useWorkspaceContext();
  const { mutateAsync: addSparkStep, isLoading: isAddingSparkStepLoading } =
    useAddSparkStep();
  const { handleRouteBack, navigate } = useSafeNavigate();

  const { sparkStepTypes } = useSparkStepTypes();

  const {
    open: isCreateWithAIModalOpen,
    openDialog: openCreateWithAIModal,
    closeDialog: closeCreateWithAIModal,
  } = useDialog();

  const setSelectedStep = useSparkBuilderStore(
    (store) => store.builderSideBarActions.setSelectedStep
  );
  const promptFunction = sparkStepTypes.find(
    (type) => type.attributes.sortOrder === 1
  );
  const disabled = isAddSparkLoading || isAddingSparkStepLoading;

  const createBasicSparkWithPromptFunction = async () => {
    if (!promptFunction?.id) return;
    const spark = {
      workspaceID: currentWorkspaceID || '',
      sparkPrivacyTypeID: 1,
      nameforHuman: sparkName,
    };
    const response = await addSpark(spark);

    if (response.data.id) {
      const sparkID = response.data.id;
      const id = v4();
      const newStep = {
        id,
        stepOrder: 1,
        stepTypeID: promptFunction?.id,
        sparkID,
      };
      await addSparkStep(newStep);
      setSelectedStep({ type: 'sparkStep', id });
      addSelectedNodes([id]);
      navigate(`${SPARK_ROUTES.SPARK_BUILDER}/${sparkID}`);
    }
  };

  const onCardClick = async (action: ActionType) => {
    if (disabled) {
      return;
    }

    switch (action) {
      case ACTIONS.START_FROM_SCRATCH:
        await createBasicSparkWithPromptFunction();
        break;
      case ACTIONS.EXTEND_A_SPARK:
        break;
      case ACTIONS.USE_A_TEMPLATE:
        break;
      case ACTIONS.CREATE_WITH_AI:
        openCreateWithAIModal();
        break;
      default:
    }
  };

  const handleUpdateValue = (value: string) => {
    setSparkName(value);
  };

  return (
    <>
      <CreateSparkHeaderOnboarding
        value={sparkName}
        setSparkName={handleUpdateValue}
      />
      <Grid item bgcolor={(theme) => theme.palette.bgGray.light}>
        <Box marginLeft={1.5} marginTop={1}>
          <Button
            onClick={handleRouteBack}
            sx={{
              minWidth: 'unset',
              width: '45px',
              height: '45px',
              padding: 0,
              borderWidth: '1px',
              borderRadius: '14px',
              borderColor: '#EEEEEE',
              aspectRatio: 1,
            }}
            variant='outlined'
          >
            <BackIcon />
          </Button>
        </Box>
        <Box
          sx={{ flexGrow: 1 }}
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          height='calc(100vh - 4rem)'
          maxWidth={'100%'}
          gap={4}
        >
          <Typography variant='h5' fontFamily='Mont-Semibold'>
            Select an option to get started
          </Typography>
          <Grid item container justifyContent='center' gap={3}>
            {cards.map((card, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '10px',
                  width: '220px',
                  aspectRatio: 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.5 : 1,
                  backgroundColor: 'var(--color-white)',
                  outline: (theme) => `1px solid ${theme.palette.borderColor}`,
                  boxShadow: 'none',
                  transition: 'none',
                  '&:hover': {
                    backgroundColor: 'var(--color-white)',
                    boxShadow: 'none',
                    outline: '1px solid var( --color-primary)',
                    transition: 'none',
                  },
                }}
              >
                <Box
                  sx={{
                    padding: '16px',
                    pointerEvents: disabled ? 'none' : 'auto',
                    backgroundColor: 'var(--color-white)',
                    '&:hover': {
                      boxShadow: 'none',
                      backgroundColor: 'inherit',
                      transition: 'none',
                    },
                  }}
                  onClick={() => !disabled && onCardClick(card.action)}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      flexDirection: 'column',
                    }}
                  >
                    <Box
                      sx={{
                        width: '50px',
                        height: '50px',
                        borderRadius: 1,
                        background: (theme) =>
                          disabled
                            ? theme.palette.grey[300]
                            : theme.palette.primary.light,
                        marginBottom: 2,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      {card.icon}
                    </Box>
                    <Typography
                      sx={{
                        textWrap: 'pretty',
                        color: disabled ? 'text.disabled' : 'inherit',
                      }}
                      fontFamily='Mont-semibold'
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant='subtitle2'
                      sx={{
                        textWrap: 'wrap',
                        textAlign: 'center',
                        color: disabled ? 'text.disabled' : 'inherit',
                      }}
                    >
                      {card.text}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))}
          </Grid>
        </Box>
      </Grid>
      <CreateWithAI
        open={isCreateWithAIModalOpen}
        onClose={closeCreateWithAIModal}
      />
    </>
  );
}

export default CreateSparkOnboarding;
